import React from 'react';
import { FileText, Layers } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useLanguage } from '../../../features/language/useLanguage';

export type CreateWorkOrderMode = 'single' | 'bulk';

interface Props {
  mode: CreateWorkOrderMode;
  onChange: (next: CreateWorkOrderMode) => void;
}

export const CreateWorkOrderModeToggle: React.FC<Props> = ({ mode, onChange }) => {
  const { t } = useLanguage();

  return (
    <div
      role="tablist"
      aria-label={t('workOrders.bulk.modeLabel')}
      className="relative inline-flex rounded-lg bg-gray-100 p-1 ring-1 ring-inset ring-gray-200/80"
      data-test-id="wo-create-mode-toggle"
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-md bg-primary-500 shadow-sm ring-1 ring-inset ring-primary-600/20 transition-transform duration-200 ease-out',
          mode === 'bulk' && 'translate-x-full',
        )}
      />
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'single'}
        onClick={() => onChange('single')}
        className={cn(
          'relative z-10 inline-flex min-w-[112px] items-center justify-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors',
          mode === 'single' ? 'text-white' : 'text-gray-500 hover:text-gray-800',
        )}
        data-test-id="wo-create-mode-single"
      >
        <FileText className="h-4 w-4" />
        {t('workOrders.bulk.modeSingle')}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'bulk'}
        onClick={() => onChange('bulk')}
        className={cn(
          'relative z-10 inline-flex min-w-[112px] items-center justify-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors',
          mode === 'bulk' ? 'text-white' : 'text-gray-500 hover:text-gray-800',
        )}
        data-test-id="wo-create-mode-bulk"
      >
        <Layers className="h-4 w-4" />
        {t('workOrders.bulk.modeBulk')}
      </button>
    </div>
  );
};
